import { useCallback, useEffect, useState } from 'react';

import { useDebouncedValue } from '@kc-console/core';
import IcSearchRegular from '@kep/kcds-icons/IcSearchRegular';

import { TextInput } from '../TextInput';

import type { ChangeEvent, KeyboardEvent } from 'react';
import type { TextFilterProps } from './types';

const TextFilter = (props: TextFilterProps) => {
  const {
    filter,
    inputRef,
    onFilterChange,
    triggerOption = 'debounce',
    debounceDelay = 500,
    filterWidth = 360,
    onChange,
    onKeyDown,
    ref,
    ...others
  } = props;
  const [value, setValue] = useState<string>(() => (typeof filter === 'string' ? filter : ''));
  const debounceValue = useDebouncedValue(value, debounceDelay);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(event);
      }

      setValue(event.currentTarget.value);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    setValue('');
    if (triggerOption === 'entered') {
      onFilterChange('');
    }
  }, [triggerOption, onFilterChange]);

  const handleKeyDownEnter = useCallback(
    (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (onKeyDown) {
        onKeyDown(event);
      }

      if (triggerOption !== 'entered') return;

      if (event.key === 'Enter') {
        onFilterChange(event.currentTarget.value);
      }
    },
    [onFilterChange, onKeyDown, triggerOption],
  );

  useEffect(() => {
    setValue(typeof filter === 'string' ? filter : '');
  }, [filter]);

  useEffect(() => {
    if (triggerOption === 'debounce') {
      onFilterChange(debounceValue);
    }
  }, [debounceValue, onFilterChange, triggerOption]);

  return (
    <TextInput
      ref={ref}
      inputRef={inputRef}
      value={value}
      onChange={handleChange}
      onClear={handleClear}
      onKeyDown={handleKeyDownEnter}
      startAdornment={<IcSearchRegular />}
      size="md"
      sx={{ width: filterWidth }}
      {...others}
    />
  );
};

export default TextFilter;
